import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { useToast } from '../hooks/use-toast';
import { 
  HardHat, Server, Warehouse, Factory, CheckCircle2, Clock, 
  FileText, Shield, Zap, ChevronRight, Loader2, Play, BookOpen,
  AlertTriangle, Target, Eye, Download, ArrowRight, Sparkles
} from 'lucide-react';
import { Progress } from './ui/progress';

const API = process.env.REACT_APP_BACKEND_URL;

const INDUSTRY_ICONS = {
  construction: HardHat,
  data_center: Server,
  warehouse: Warehouse,
  manufacturing: Factory
};

const INDUSTRY_COLORS = {
  construction: 'from-orange-500 to-amber-600',
  data_center: 'from-blue-500 to-cyan-600',
  warehouse: 'from-purple-500 to-indigo-600',
  manufacturing: 'from-green-500 to-emerald-600'
};

const SafetyProgramsPage = () => {
  const { toast } = useToast();
  const [programs, setPrograms] = useState([]);
  const [activatedPrograms, setActivatedPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [activating, setActivating] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [programsRes, activatedRes] = await Promise.all([
        axios.get(`${API}/api/programs`),
        axios.get(`${API}/api/programs/activated/list`)
      ]);
      setPrograms(programsRes.data.programs || []);
      setActivatedPrograms(activatedRes.data.activated_programs || []);
    } catch (error) {
      console.error('Error fetching programs:', error);
    } finally {
      setLoading(false);
    }
  };

  const viewProgramDetails = async (programId) => {
    try {
      const response = await axios.get(`${API}/api/programs/${programId}`);
      setSelectedProgram(response.data);
      setShowDetailDialog(true);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to load program details', variant: 'destructive' });
    }
  };

  const activateProgram = async (programId) => {
    setActivating(true);
    try {
      const response = await axios.post(`${API}/api/programs/${programId}/activate`);
      toast({ 
        title: 'Program Activated!', 
        description: `${response.data.checklists_created} checklists created. Start with quick setup steps.`
      });
      setShowDetailDialog(false);
      fetchData();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to activate program', variant: 'destructive' });
    } finally {
      setActivating(false);
    }
  };

  const isActivated = (programId) => {
    return activatedPrograms.some(p => p.program_id === programId);
  };

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-6" data-testid="safety-programs-page">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 bg-blue-100 rounded-lg">
            <Shield className="w-8 h-8 text-blue-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">OSHA Safety Programs</h1>
            <p className="text-slate-500">Pre-built compliance packages - Get compliant in 15 minutes</p>
          </div>
        </div>
      </div>

      {/* Value Proposition Banner */}
      <Card className="mb-8 bg-gradient-to-r from-blue-600 to-indigo-700 text-white border-0">
        <CardContent className="py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <Sparkles className="w-12 h-12 text-yellow-300" />
              <div>
                <h2 className="text-xl font-bold">OSHA-First, Not Form-First</h2>
                <p className="text-blue-100">
                  Unlike SafetyCulture and Field1st, we codify OSHA standards directly into workflows. 
                  No configuration needed.
                </p>
              </div>
            </div>
            <div className="flex gap-4 text-center">
              <div className="px-4 py-2 bg-white/20 rounded-lg">
                <div className="text-2xl font-bold">15 min</div>
                <div className="text-xs text-blue-100">Setup Time</div>
              </div>
              <div className="px-4 py-2 bg-white/20 rounded-lg">
                <div className="text-2xl font-bold">100%</div>
                <div className="text-xs text-blue-100">OSHA Coverage</div>
              </div>
              <div className="px-4 py-2 bg-white/20 rounded-lg">
                <div className="text-2xl font-bold">AI</div>
                <div className="text-xs text-blue-100">Powered Scans</div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Programs Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {programs.map(program => {
            const Icon = INDUSTRY_ICONS[program.industry] || Shield;
            const gradient = INDUSTRY_COLORS[program.industry] || 'from-gray-500 to-slate-600';
            const activated = isActivated(program.id);
            
            return (
              <Card 
                key={program.id} 
                className={`overflow-hidden hover:shadow-lg transition-shadow ${activated ? 'ring-2 ring-green-500' : ''}`}
              >
                <div className={`h-2 bg-gradient-to-r ${gradient}`} />
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`p-3 rounded-lg bg-gradient-to-r ${gradient}`}>
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <CardTitle className="text-lg">{program.name}</CardTitle>
                        <CardDescription>{program.target_audience}</CardDescription>
                      </div>
                    </div>
                    {activated && (
                      <Badge className="bg-green-100 text-green-700">
                        <CheckCircle2 className="w-3 h-3 mr-1" /> Active
                      </Badge>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-slate-600 mb-4">{program.description}</p>
                  
                  <div className="flex flex-wrap gap-2 mb-4">
                    {program.osha_standards?.map((std, i) => (
                      <Badge key={i} variant="outline" className="text-xs">{std}</Badge>
                    ))}
                  </div>
                  
                  <div className="grid grid-cols-3 gap-4 mb-4 text-center">
                    <div className="p-2 bg-slate-50 rounded-lg">
                      <div className="text-lg font-bold text-slate-900">{program.module_count}</div>
                      <div className="text-xs text-slate-500">Modules</div>
                    </div>
                    <div className="p-2 bg-slate-50 rounded-lg">
                      <div className="text-lg font-bold text-slate-900">{program.checklist_count}</div>
                      <div className="text-xs text-slate-500">Checklists</div>
                    </div>
                    <div className="p-2 bg-slate-50 rounded-lg">
                      <div className="text-lg font-bold text-slate-900">{program.setup_time}</div>
                      <div className="text-xs text-slate-500">Setup</div>
                    </div>
                  </div>
                  
                  <div className="flex gap-2">
                    <Button 
                      className="flex-1" 
                      variant="outline"
                      onClick={() => viewProgramDetails(program.id)}
                    >
                      <Eye className="w-4 h-4 mr-2" /> View Details
                    </Button>
                    {!activated && (
                      <Button 
                        className={`flex-1 bg-gradient-to-r ${gradient} hover:opacity-90`}
                        onClick={() => viewProgramDetails(program.id)}
                      >
                        <Play className="w-4 h-4 mr-2" /> Activate
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Program Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          {selectedProgram && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-3">
                  {React.createElement(INDUSTRY_ICONS[selectedProgram.industry] || Shield, {
                    className: "w-8 h-8 text-blue-600"
                  })}
                  <div>
                    <DialogTitle>{selectedProgram.name}</DialogTitle>
                    <DialogDescription>{selectedProgram.description}</DialogDescription>
                  </div>
                </div>
              </DialogHeader>
              
              <Tabs defaultValue="modules" className="mt-4">
                <TabsList>
                  <TabsTrigger value="modules">Modules ({selectedProgram.modules?.length})</TabsTrigger>
                  <TabsTrigger value="quickstart">Quick Start</TabsTrigger>
                  <TabsTrigger value="documentation">Documentation</TabsTrigger>
                </TabsList>
                
                <TabsContent value="modules" className="space-y-4 mt-4">
                  {selectedProgram.modules?.map((module, i) => (
                    <Card key={i}>
                      <CardHeader className="pb-2">
                        <div className="flex items-center justify-between">
                          <CardTitle className="text-base flex items-center gap-2">
                            <Target className="w-4 h-4 text-blue-600" />
                            {module.name}
                          </CardTitle>
                          <Badge variant="outline">{module.osha_ref}</Badge>
                        </div>
                        <CardDescription>{module.description}</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <h4 className="font-medium text-sm mb-2 flex items-center gap-1">
                              <FileText className="w-4 h-4" /> Checklists
                            </h4>
                            {module.checklists?.map((cl, j) => (
                              <div key={j} className="text-sm text-slate-600 flex items-center gap-1">
                                <CheckCircle2 className="w-3 h-3 text-green-500" />
                                {cl.name} ({cl.items?.length} items)
                              </div>
                            ))}
                          </div>
                          <div>
                            <h4 className="font-medium text-sm mb-2 flex items-center gap-1">
                              <BookOpen className="w-4 h-4" /> Training Topics
                            </h4>
                            {module.training_topics?.slice(0, 3).map((topic, j) => (
                              <div key={j} className="text-sm text-slate-600">• {topic}</div>
                            ))}
                          </div>
                          <div>
                            <h4 className="font-medium text-sm mb-2 flex items-center gap-1">
                              <Zap className="w-4 h-4" /> AI Scan Profile
                            </h4>
                            {module.ai_scan_profile && (
                              <div className="text-sm text-slate-600">
                                <div className="font-medium">{module.ai_scan_profile.name}</div>
                                <div className="text-xs">
                                  Detects: {module.ai_scan_profile.detect?.slice(0, 3).join(', ')}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </TabsContent>
                
                <TabsContent value="quickstart" className="mt-4">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Quick Start Guide</CardTitle>
                      <CardDescription>Get compliant in {selectedProgram.setup_time}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {selectedProgram.quick_start?.map((step, i) => (
                          <div key={i} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                            <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold">
                              {i + 1}
                            </div>
                            <span className="text-slate-700">{step}</span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>
                
                <TabsContent value="documentation" className="mt-4">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Required Documentation</CardTitle>
                      <CardDescription>
                        Retention: {selectedProgram.documentation?.retention_period}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {selectedProgram.documentation?.required_records?.map((record, i) => (
                          <div key={i} className="flex items-center gap-2 text-sm">
                            <FileText className="w-4 h-4 text-slate-400" />
                            {record}
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>
              </Tabs>
              
              {!isActivated(selectedProgram.id) && (
                <div className="flex justify-end gap-2 mt-6 pt-4 border-t">
                  <Button variant="outline" onClick={() => setShowDetailDialog(false)}>
                    Close
                  </Button>
                  <Button 
                    onClick={() => activateProgram(selectedProgram.id)}
                    disabled={activating}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {activating ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Play className="w-4 h-4 mr-2" />
                    )}
                    Activate Program
                  </Button>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SafetyProgramsPage;
